import { useEffect } from 'react';
import { useThree } from '@react-three/fiber';

/**
 * Plan 07-05 / PERF-03 — exposes `window.__rendererInfo()` ONLY when the
 * `?probe=1` query parameter is present, letting the Playwright perf spec
 * (perf-draw-calls.spec.ts) sample gl.info.render during a scripted walk.
 *
 * Gating:
 *   - Reads `window.location.search` once on mount — no per-frame cost when off.
 *   - Writes the getter on mount + cleans up on unmount so the browser never
 *     carries a dangling reference to the r3f state object.
 *
 * Mount: inside the R3F Canvas as a null-returning Canvas-scope component
 * (useThree requires Canvas scope). SceneGraph.tsx is the canonical mount
 * point — it's the single place where every world object composes.
 */
export function DrawCallProbe() {
  const gl = useThree((state) => state.gl);

  useEffect(() => {
    const enabled = new URLSearchParams(window.location.search).get('probe') === '1';
    if (!enabled) return;

    (window as unknown as { __rendererInfo?: () => unknown }).__rendererInfo = () => ({
      calls: gl.info.render.calls,
      triangles: gl.info.render.triangles,
      points: gl.info.render.points,
      lines: gl.info.render.lines,
      memory: {
        geometries: gl.info.memory.geometries,
        textures: gl.info.memory.textures,
      },
    });

    return () => {
      delete (window as unknown as { __rendererInfo?: unknown }).__rendererInfo;
    };
  }, [gl]);

  return null;
}
